/**
 * A gaveta de UM redirecionador — onde a tela deixa de ser um CRUD e vira produto.
 *
 * Três abas, nesta ordem, que é a ordem das perguntas de quem abre:
 *
 *  1. **Instalação** — "qual é o link e o que eu faço com ele?". É a aba que abre primeiro,
 *     porque um link criado e não instalado não vale nada, e porque é aqui que mora o
 *     conserto do `NO_ORIGIN` (ADR-0013).
 *  2. **Cliques** — "o que aconteceu com quem clicou?". Cada linha traz a ORIGEM e o MOTIVO
 *     da alocação, que é a auditoria do allocator em português.
 *  3. **Quem clicou** — "quem entrou por este link, e quanto ficou?" (C1–C4): o clique ligado
 *     à pessoa, com aparelho, suspeita, confiança e região. Mora em `WhoClickedPanel`.
 *  4. **Próximos grupos** — "para onde vai o próximo?". Retrato sem reserva: pode mudar no
 *     instante seguinte, e a aba diz isso em vez de fingir precisão.
 */
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTrigger,
  useToast,
} from '@mgdlead/ui';
import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Check, MousePointerClick, RefreshCw, Users } from 'lucide-react';

import { FormAlert } from '@mgdlead/ui';
import { CopyBlock } from './CopyBlock';
import { LandingPageTab } from './LandingPageTab';
import { VARIANTE } from '../../../features/redirector/landing-ui';
import { OriginDiagnosisPanel } from './OriginDiagnosisPanel';
import { WhoClickedPanel } from './WhoClickedPanel';
import {
  CLICK_ORIGIN,
  CLICK_STATUS,
  POR_QUE_PREVIAS,
  PREVIEW_HEADLINE,
  READINESS,
  avaliarEntrega,
  destinoSemGrupo,
  efeitoDeNenhumPronto,
  janelaEmTexto,
  previasEmTexto,
  resumoDaEntrega,
  targetVerdict,
  enderecoPublico,
} from '../../../features/redirector/redirector-ui';
import type { DeliveryReadiness, ReadinessRow } from '../../../features/redirector/redirector-ui';
import { INVITE_WHY_REFRESH, inviteRefreshSummary } from '../../../features/group/group-ui';
import {
  POR_QUE_O_TRECHO,
  buildInstallSnippet,
} from '../../../features/redirector/install-snippet';
import {
  useCampaignGroups,
  useCreateRedirectorTestLink,
  useGroups,
  useOriginSummary,
  useRedirectorClicks,
  useRedirectorPreviewHits,
  useRedirectorTargets,
  useRefreshGroupInvite,
} from '../../../lib/api';
import type {
  ClickFilters,
  Redirector,
  RedirectorClick,
  RedirectorPreviewHits,
  RedirectorTarget,
  RedirectorTestLink,
} from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';

export interface RedirectorDrawerProps {
  workspaceId: string;
  timezone: string;
  redirector: Redirector | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `group:update`: é a permissão que o servidor cobra para rebuscar um convite. */
  canRefreshInvite?: boolean;
  /** `redirector:update`: a permissão que o servidor cobra para gerar o link de teste (B1). */
  canTestLink?: boolean;
  /** `redirector:page` (OWNER/ADMIN): editar a página de passagem, pixels e prévia (B2–B4). */
  canEditPage?: boolean;
  /** `pii:reveal`: mostra o botão de revelar telefone na aba Quem clicou. */
  canRevealPhone?: boolean;
}

export function RedirectorDrawer({
  workspaceId,
  timezone,
  redirector,
  open,
  onOpenChange,
  canRefreshInvite = false,
  canTestLink = false,
  canEditPage = false,

  canRevealPhone = false,
}: RedirectorDrawerProps) {
  const { toast } = useToast();
  const id = redirector?.id ?? null;
  /**
   * Os filtros da lista de cliques.
   *
   * Eles vão para o SERVIDOR (`redirector.clicks` aceita `origin` e `status`), e não são um
   * `.filter()` nesta lista: filtrar depois de paginar devolveria "nenhum sem origem" numa
   * página de 50 que por acaso não tinha nenhum — e a conclusão errada seria "o link está
   * bom". É exatamente o caso de quem abre esta gaveta para depurar uma instalação.
   */
  const [filtros, setFiltros] = useState<ClickFilters>({});
  const clicksQuery = useRedirectorClicks(workspaceId, open ? id : null, 50, filtros);

  /**
   * A janela do resumo por origem: os últimos 30 dias, fechados AQUI e não no servidor.
   *
   * É a exceção deliberada à regra do painel (lá o período é resolvido no fuso do workspace):
   * este recorte é "os últimos 30 × 24 h", uma janela rolante de instalação, não um dia do
   * negócio. `useMemo` com `open` na dependência porque um `new Date()` solto no corpo do
   * componente mudaria a chave da query a cada render e a consulta nunca pararia de refazer.
   */
  const janela = useMemo(() => {
    const to = new Date();
    return { from: new Date(to.getTime() - 30 * 86_400_000), to };
  }, [open, id]);
  const origemQuery = useOriginSummary(workspaceId, janela, open ? id : null);
  const targetsQuery = useRedirectorTargets(workspaceId, open ? id : null);
  /** A1 — prévias de robô dos últimos 30 dias do workspace, à parte dos cliques. */
  const previasQuery = useRedirectorPreviewHits(workspaceId, open ? id : null, 30);

  /* ---------------------------------------------------------------------- */
  /*  Os grupos deste link estão prontos para receber gente?                  */
  /* ---------------------------------------------------------------------- */

  /**
   * **A pergunta que precisa ser respondida ANTES de o endereço ir para o anúncio.**
   *
   * Por isso ela abre a aba de instalação, em cima do endereço: quem copia o link primeiro e
   * descobre depois já gastou dinheiro. E por isso são duas consultas — `campaign.groups` diz
   * quais grupos o link alcança, `group.list` é a única que traz o `inviteCode`, e é o convite
   * que decide se o grupo recebe alguém.
   *
   * `enabled` pelas duas: a gaveta só pergunta quando está aberta.
   */
  const campaignGroupsQuery = useCampaignGroups(
    workspaceId,
    open && redirector !== null ? redirector.campaignId : null,
  );
  const groupsQuery = useGroups(workspaceId);

  const entrega = useMemo((): DeliveryReadiness | null => {
    const grupos = campaignGroupsQuery.data;
    if (grupos == null) return null;
    const lista = groupsQuery.data?.groups;
    return avaliarEntrega(grupos, (groupId) => {
      // `undefined` = ainda não sabemos. Nunca `null` (que a tela leria como "sem convite").
      if (lista == null) return undefined;
      const grupo = lista.find((g) => g.id === groupId);
      return grupo === undefined ? undefined : grupo.inviteCode;
    });
  }, [campaignGroupsQuery.data, groupsQuery.data]);

  const refreshInvite = useRefreshGroupInvite();
  const buscarConvite = useCallback(
    (groupId: string) => {
      refreshInvite.mutate(
        { workspaceId, groupId },
        {
          onSuccess: (resultado) => {
            /*
             * `changed: false` NÃO é fracasso: quer dizer "o convite continua o mesmo", que é
             * boa notícia. Ver `inviteRefreshSummary` — o tom é de sucesso nos três desfechos.
             */
            const resumo = inviteRefreshSummary(resultado);
            toast({ title: resumo.title, description: resumo.description, tone: resumo.tone });
          },
          onError: (error: unknown) =>
            toast({
              title: 'Não deu para buscar o convite',
              description: humanizeError(error),
              tone: 'danger',
            }),
        },
      );
    },
    [refreshInvite, toast, workspaceId],
  );

  // O endereço vem PRONTO do servidor (`publicUrl`): a tela não monta mais o link a partir
  // de uma env própria. Ver `enderecoPublico`.
  const link = redirector === null ? null : enderecoPublico(redirector);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={redirector?.name ?? 'Redirecionador'}
      description={
        redirector === null
          ? undefined
          : `Campanha ${redirector.campaignName} · janela de atribuição de ${janelaEmTexto(redirector.attributionWindowMinutes)}`
      }
    >
      {redirector === null ? null : (
        <Tabs defaultValue="instalacao">
          <TabsList label="O que ver deste link">
            <TabsTrigger value="instalacao">Instalação</TabsTrigger>
            <TabsTrigger value="cliques">Cliques</TabsTrigger>
            <TabsTrigger value="quem-clicou">Quem clicou</TabsTrigger>
            <TabsTrigger value="proximos">Próximos grupos</TabsTrigger>
            <TabsTrigger value="pagina">Página</TabsTrigger>
          </TabsList>

          {/* ------------------------------ instalação ------------------------------ */}
          <TabsPanel value="instalacao" className="flex flex-col gap-4">
            <OriginDiagnosisPanel redirector={redirector} />

            <EntregaSection
              entrega={entrega}
              temFallback={redirector.fallbackUrl !== null}
              podeBuscar={canRefreshInvite}
              buscando={refreshInvite.isPending}
              onBuscar={buscarConvite}
            />

            <section className="flex flex-col gap-2 border-t border-border pt-4">
              <h3 className="text-md font-medium">2. O endereço do link</h3>
              {link === null ? (
                <SemBasePublica slug={redirector.slug} />
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    É este endereço que vai no botão “Entrar no grupo” da sua página. Ele não muda
                    nunca — nem se você renomear o link depois.
                  </p>
                  <CopyBlock value={link} what="Endereço" block />
                </>
              )}
            </section>

            {link === null ? null : (
              <section className="flex flex-col gap-2 border-t border-border pt-4">
                <h3 className="text-md font-medium">3. O trecho que vai na sua página</h3>
                <p className="text-sm text-muted-foreground">{POR_QUE_O_TRECHO}</p>
                <CopyBlock value={buildInstallSnippet(link)} what="Trecho" variant="pre" block />
                <p className="text-2xs text-muted-foreground">
                  Se a sua página é feita no Elementor, Webflow, Framer ou Typebot, procure a opção
                  “passar parâmetros de URL” (ou <em>URL parameter forwarding</em>): ligar essa
                  opção resolve a mesma coisa, sem colar código.
                </p>
              </section>
            )}

            <section className="flex flex-col gap-2 border-t border-border pt-4">
              <h3 className="text-md font-medium">4. Para onde o clique cai sem grupo</h3>
              <p className="text-sm text-muted-foreground">{destinoSemGrupo(redirector)}</p>
            </section>

            {canTestLink && link !== null ? (
              <LinkDeTeste workspaceId={workspaceId} redirectorId={redirector.id} />
            ) : null}
          </TabsPanel>

          {/* -------------------------------- cliques ------------------------------- */}
          <TabsPanel value="cliques" className="flex flex-col gap-3">
            <OrigemNoPeriodo dados={origemQuery.data} carregando={origemQuery.isPending} />
            <PreviasNoPeriodo
              dados={previasQuery.data}
              carregando={previasQuery.isPending}
              erro={previasQuery.isError}
            />

            <ClickFilterBar value={filtros} onChange={setFiltros} />

            {clicksQuery.isPending ? (
              <ListaCarregando />
            ) : clicksQuery.isError ? (
              <ErrorState
                title="Não conseguimos carregar os cliques"
                description={humanizeError(clicksQuery.error)}
                onRetry={() => void clicksQuery.refetch()}
              />
            ) : (clicksQuery.data ?? []).length === 0 ? (
              /*
               * Duas ausências diferentes, e duas frases diferentes (invariante 8):
               * com filtro, "não há clique ASSIM" — o link pode estar cheio de cliques;
               * sem filtro, "não há clique NENHUM". Uma frase só faria alguém concluir que o
               * link nunca foi usado logo depois de filtrar por "sem origem", que é
               * justamente a boa notícia.
               */
              <EmptyState
                kind={temFiltro(filtros) ? 'zero' : 'no-data'}
                icon={MousePointerClick}
                title={temFiltro(filtros) ? 'Nenhum clique com esse filtro' : 'Nenhum clique ainda'}
                description={
                  temFiltro(filtros)
                    ? 'O link pode ter recebido cliques — nenhum deles se encaixa neste recorte. Limpe o filtro para ver todos.'
                    : 'Assim que alguém clicar no link, cada clique aparece aqui com a origem e o motivo de ter ido para aquele grupo.'
                }
              />
            ) : (
              <>
                <p className="text-2xs text-muted-foreground">
                  Os {clicksQuery.data?.length} cliques mais recentes, do mais novo para o mais
                  antigo.
                </p>
                <ul className="flex flex-col divide-y divide-border">
                  {(clicksQuery.data ?? []).map((click) => (
                    <li key={click.id}>
                      <ClickRow click={click} timezone={timezone} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </TabsPanel>

          {/* -------------------------- página de passagem -------------------------- */}
          <TabsPanel value="pagina" className="flex flex-col gap-3">
            {open ? (
              <LandingPageTab
                workspaceId={workspaceId}
                redirectorId={redirector.id}
                publicUrl={link}
                canEdit={canEditPage}
              />
            ) : null}
          </TabsPanel>

          {/* ------------------------------ quem clicou ----------------------------- */}
          <TabsPanel value="quem-clicou">
            {open ? (
              <WhoClickedPanel
                workspaceId={workspaceId}
                redirectorId={redirector.id}
                timezone={timezone}
                canRevealPhone={canRevealPhone}
              />
            ) : null}
          </TabsPanel>

          {/* ------------------------------- próximos ------------------------------- */}
          <TabsPanel value="proximos" className="flex flex-col gap-3">
            {/*
              A FRASE MUDA COM A ESTRATÉGIA, e isso não é estilo: `isNext` quer dizer "pode
              receber o próximo clique", e quantos vêm `true` depende da estratégia — um no
              sequencial, só o fixado no grupo fixo, TODOS os que cabem no sorteio. A frase
              antiga ("a fila, na ordem em que o link enche cada um") valia enquanto só existia
              `FILL_SEQUENTIAL`; com sorteio ligado ela apontaria um grupo que o allocator
              ignora, sem erro e sem log.
            */}
            <p className="text-sm text-muted-foreground">{PREVIEW_HEADLINE[redirector.strategy]}</p>
            <p className="text-2xs text-muted-foreground">
              É um retrato, não uma reserva: vaga segurada por outro clique some da conta sozinha
              depois de 15 minutos.
            </p>

            {targetsQuery.isPending ? (
              <ListaCarregando />
            ) : targetsQuery.isError ? (
              <ErrorState
                title="Não conseguimos carregar a fila de grupos"
                description={humanizeError(targetsQuery.error)}
                onRetry={() => void targetsQuery.refetch()}
              />
            ) : (targetsQuery.data ?? []).length === 0 ? (
              <EmptyState
                kind="zero"
                icon={Users}
                title="Nenhum grupo entra nesta conta"
                description="A campanha deste link não tem nenhum grupo vinculado que esteja sendo monitorado, no rodízio e com vaga. Quem clicar agora vai para o destino de reserva. (O convite não entra nesta lista: ele é conferido na aba Instalação, logo acima do endereço.)"
              />
            ) : (
              <Table caption="Grupos que este redirecionador alcança">
                <TableHeader>
                  <TableRow>
                    <TableHead>Grupo</TableHead>
                    <TableHead numeric>Cabe</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(targetsQuery.data ?? []).map((target) => (
                    <TargetRow key={target.groupId} target={target} />
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsPanel>
        </Tabs>
      )}
    </Drawer>
  );
}

/* ========================================================================== */
/*  Pedaços                                                                    */
/* ========================================================================== */

/**
 * O painel não sabe o host público do redirecionador.
 *
 * Preferimos DIZER isso a montar um endereço plausível: o link seria copiado para dentro de
 * um anúncio pago e o erro só apareceria depois do dinheiro gasto.
 */
/** `true` quando algum filtro está ativo. É o que separa "não há assim" de "não há nenhum". */
function temFiltro(f: ClickFilters): boolean {
  return f.origin !== undefined || f.status !== undefined;
}

/**
 * Os filtros da lista de cliques: origem e desfecho.
 *
 * Chips e não `<select>`: são poucas opções, todas cabem, e o alvo de toque de um chip é maior
 * que o de um select nativo no celular. E o estado ativo é visível sem abrir nada — quem
 * filtrou por "sem origem" precisa VER que filtrou antes de concluir que o link não tem clique.
 */
function ClickFilterBar({
  value,
  onChange,
}: {
  value: ClickFilters;
  onChange: (next: ClickFilters) => void;
}) {
  const origens = Object.entries(CLICK_ORIGIN) as [RedirectorClick['origin'], { label: string }][];
  const status = Object.entries(CLICK_STATUS) as [RedirectorClick['status'], { label: string }][];

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-2">
      <FiltroLinha
        rotulo="Origem"
        opcoes={origens}
        ativo={value.origin}
        onPick={(v) =>
          onChange(v === undefined ? omitir(value, 'origin') : { ...value, origin: v })
        }
      />
      <FiltroLinha
        rotulo="Desfecho"
        opcoes={status}
        ativo={value.status}
        onPick={(v) =>
          onChange(v === undefined ? omitir(value, 'status') : { ...value, status: v })
        }
      />
    </div>
  );
}

function omitir<K extends keyof ClickFilters>(f: ClickFilters, chave: K): ClickFilters {
  const copia = { ...f };
  delete copia[chave];
  return copia;
}

function FiltroLinha<T extends string>({
  rotulo,
  opcoes,
  ativo,
  onPick,
}: {
  rotulo: string;
  opcoes: [T, { label: string }][];
  ativo: T | undefined;
  onPick: (value: T | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {rotulo}
      </span>
      <Chip label="Todos" ativo={ativo === undefined} onClick={() => onPick(undefined)} />
      {opcoes.map(([valor, spec]) => (
        <Chip
          key={valor}
          label={spec.label}
          ativo={ativo === valor}
          onClick={() => onPick(ativo === valor ? undefined : valor)}
        />
      ))}
    </div>
  );
}

function Chip({ label, ativo, onClick }: { label: string; ativo: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={
        ativo
          ? 'rounded-sm bg-primary px-2 py-1 text-2xs font-medium text-primary-foreground'
          : 'rounded-sm border border-border bg-surface px-2 py-1 text-2xs font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      }
    >
      {label}
    </button>
  );
}

/**
 * Origem dos cliques **nos últimos 30 dias**, contada no servidor.
 *
 * Era o pedido que faltava: os contadores da lista (`clicksTotal`, `clicksNoOrigin`) são de
 * VIDA INTEIRA do link, e o acumulado some justamente com a melhora — quem consertou o botão
 * da landing page na terça continuaria vendo "280 sem origem" na sexta, porque os 280 de
 * antes nunca saem da conta. Recortado por período, o mesmo link mostra "0 sem origem nos
 * últimos 30 dias", que é a frase que prova que o conserto funcionou.
 *
 * `lastClickAt === null` é "nenhum clique NESTE recorte" e vira frase, não `0%`.
 */
function OrigemNoPeriodo({
  dados,
  carregando,
}: {
  dados:
    | {
        total: number;
        metaAds: number;
        tagged: number;
        noOrigin: number;
        lastClickAt: string | Date | null;
      }
    | undefined;
  carregando: boolean;
}) {
  if (carregando || !dados) return <Skeleton className="h-14 w-full" />;

  if (dados.lastClickAt === null || dados.total === 0) {
    return (
      <FormAlert tone="info">
        Nenhum clique nos últimos 30 dias. Sem clique não dá para dizer nada sobre a origem — e isso
        é diferente de “nenhum com origem”.
      </FormAlert>
    );
  }

  return (
    <div className="rounded-md border border-border p-2">
      <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        Origem nos últimos 30 dias
      </p>
      <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <Par termo="Total" valor={dados.total} />
        <Par termo="De anúncio" valor={dados.metaAds} />
        <Par termo="Marcado" valor={dados.tagged} />
        <Par termo="Sem origem" valor={dados.noOrigin} />
      </dl>
      {dados.noOrigin === 0 ? (
        <p className="mt-1 text-2xs text-success">
          Todos os cliques do período chegaram com origem — a instalação está certa agora.
        </p>
      ) : null}
    </div>
  );
}

function Par({ termo, valor }: { termo: string; valor: number }) {
  return (
    <div className="flex items-baseline gap-1">
      <dt className="text-muted-foreground">{termo}</dt>
      <dd className="nums font-medium">{valor}</dd>
    </div>
  );
}

function SemBasePublica({ slug }: { slug: string }) {
  return (
    <FormAlert tone="warning">
      Este painel ainda não sabe em qual endereço os seus links são servidos, então não dá para
      montar o link completo aqui. O endereço deste redirecionador termina em{' '}
      <span className="font-mono">/r/{slug}</span> — peça a quem instalou o MGDLead para configurar
      o domínio de redirecionamento do painel.
    </FormAlert>
  );
}

function ListaCarregando() {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
      <span className="sr-only">Carregando</span>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/**
 * Uma linha de clique.
 *
 * Cartão empilhado em vez de linha de tabela porque cada clique carrega quatro informações
 * de comprimento muito diferente — hora, origem, destino e o MOTIVO escrito para gente —, e
 * espremer o motivo numa coluna de tabela é como ele deixa de ser lido.
 */
function ClickRow({ click, timezone }: { click: RedirectorClick; timezone: string }) {
  const origem = CLICK_ORIGIN[click.origin];
  const situacao = CLICK_STATUS[click.status];

  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center gap-1">
        <Badge tone={origem.tone} dot title={origem.hint}>
          {origem.label}
        </Badge>
        <Badge tone={situacao.tone} title={situacao.hint}>
          {situacao.label}
        </Badge>
        {click.landingVariant === null ? null : (
          <Badge tone="outline" title={VARIANTE[click.landingVariant].hint}>
            {VARIANTE[click.landingVariant].label}
          </Badge>
        )}
        <span
          className="nums ml-auto text-2xs text-muted-foreground"
          title={formatExact(click.clickedAt, timezone) ?? undefined}
        >
          {formatRelative(click.clickedAt)}
        </span>
      </div>

      <span className="text-sm">
        {click.groupName ?? (click.groupId === null ? 'Nenhum grupo' : 'Grupo sem nome')}
      </span>

      {/* O motivo é escrito pelo servidor, para gente ler. Não reescrevemos aqui. */}
      <span className="text-2xs text-muted-foreground">{click.allocationReason}</span>

      <span className="flex flex-wrap gap-x-3 text-2xs text-muted-foreground">
        {click.referrerHost === null ? (
          <span title="Sem referrer: o link foi aberto direto, não a partir de uma página.">
            sem página de origem
          </span>
        ) : (
          <span>veio de {click.referrerHost}</span>
        )}
        {click.metaCampaignId === null ? null : (
          <span className="font-mono">campanha Meta {click.metaCampaignId}</span>
        )}
        {click.utmSource === null ? null : (
          <span className="font-mono">utm_source={click.utmSource}</span>
        )}
      </span>
    </div>
  );
}

/**
 * Uma linha da prévia de alocação.
 *
 * `headroom === null` é **lotação desconhecida** (o grupo nunca sincronizou), e não zero — é
 * a mesma distinção que o servidor faz no motivo da alocação. Mostrar `0` aqui diria "não
 * cabe mais ninguém" sobre um grupo que talvez esteja vazio.
 *
 * O veredito da coluna "Situação" sai de `targetVerdict`, que lê a ESTRATÉGIA da linha: o
 * rótulo "Próximo" só é verdade no sequencial. Num sorteio ele vira "Pode ser este" (porque
 * pode ser qualquer um), e num grupo fixo, "Sempre este" — e os outros grupos da campanha
 * deixam de aparecer como "na fila", que prometia um desvio que o allocator não faz.
 */
function TargetRow({ target }: { target: RedirectorTarget }) {
  const veredito = targetVerdict(target);
  return (
    <TableRow>
      <TableRowHeader>{target.name ?? 'Sem nome no WhatsApp'}</TableRowHeader>
      <TableCell numeric nowrap>
        {target.headroom === null ? (
          <span className="text-muted-foreground" title="Este grupo nunca sincronizou a lotação.">
            sem dados
          </span>
        ) : (
          <span className="nums">{target.headroom}</span>
        )}
      </TableCell>
      <TableCell nowrap>
        {veredito.destaque ? (
          <Badge tone="accent" dot title={veredito.hint}>
            {veredito.label}
          </Badge>
        ) : veredito.tone === 'warning' ? (
          <Badge tone="warning" dot title={veredito.hint}>
            {veredito.label}
          </Badge>
        ) : (
          <span className="text-2xs text-muted-foreground" title={veredito.hint}>
            {veredito.label}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

/* ========================================================================== */
/*  Prontidão de entrega — a seção que abre a aba de instalação                */
/* ========================================================================== */

/**
 * **"Quantos dos grupos deste link estão prontos para receber gente, e quais não estão."**
 *
 * Esta seção existe porque um redirecionador criado hoje mandaria 100% dos cliques para o
 * destino de reserva sem nada na tela dizer: os grupos apareciam ativos e com vaga, e o
 * allocator os pulava em silêncio por falta de convite. Três decisões:
 *
 *  - **vem antes do endereço**, porque copiar o link é o passo irreversível (ele vai para
 *    dentro de um anúncio pago);
 *  - **cada grupo impedido diz o motivo e o EFEITO**, não o estado interno — "nunca recebe
 *    ninguém" em vez de "invite_code nulo";
 *  - **a ação está do lado do problema.** Um "vá até a tela do grupo" aqui seria o mesmo tipo
 *    de caminho que não existe.
 *
 * Enquanto não sabemos (`entrega === null`), a seção **não afirma nada**: um "0 de 0 prontos"
 * durante o carregamento seria mentira com cara de medição (invariante 8).
 */
function EntregaSection({
  entrega,
  temFallback,
  podeBuscar,
  buscando,
  onBuscar,
}: {
  entrega: DeliveryReadiness | null;
  temFallback: boolean;
  podeBuscar: boolean;
  buscando: boolean;
  onBuscar: (groupId: string) => void;
}) {
  return (
    <section className="flex flex-col gap-2" aria-label="Grupos deste link">
      <h3 className="text-md font-medium">1. Quem vai receber quem clicar</h3>

      {entrega === null ? (
        <Skeleton className="h-14 w-full" />
      ) : (
        <>
          <p
            className={
              entrega.nenhumPronto || entrega.semConvite > 0
                ? 'text-sm text-warning'
                : 'text-sm text-muted-foreground'
            }
          >
            {resumoDaEntrega(entrega)}
          </p>

          {entrega.nenhumPronto ? (
            <FormAlert tone="warning">{efeitoDeNenhumPronto(temFallback)}</FormAlert>
          ) : null}

          {entrega.linhas.length === 0 ? null : (
            <ul className="flex flex-col divide-y divide-border">
              {entrega.linhas.map((linha) => (
                <li key={linha.groupId}>
                  <LinhaDeEntrega
                    linha={linha}
                    podeBuscar={podeBuscar}
                    buscando={buscando}
                    onBuscar={onBuscar}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Um grupo do link, com o motivo de ele receber ou não.
 *
 * O estado se reconhece pela FORMA e não só pela cor: o grupo pronto leva um ✓, o impedido
 * leva um triângulo de alerta. E só o `SEM_CONVITE` ganha botão — é o único cuja causa está a
 * um clique de distância; pausado e cheio se resolvem na tela do grupo, e um botão para cada
 * um deles aqui devolveria a fileira de cinco botões iguais em que ninguém acha o certo.
 */
function LinhaDeEntrega({
  linha,
  podeBuscar,
  buscando,
  onBuscar,
}: {
  linha: ReadinessRow;
  podeBuscar: boolean;
  buscando: boolean;
  onBuscar: (groupId: string) => void;
}) {
  const spec = READINESS[linha.kind];
  const pronto = linha.kind === 'PRONTO';

  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {pronto ? (
          <Check aria-hidden="true" className="size-4 shrink-0 text-success" />
        ) : (
          <AlertTriangle
            aria-hidden="true"
            className={
              spec.tone === 'warning'
                ? 'size-4 shrink-0 text-warning'
                : 'size-4 shrink-0 text-muted-foreground'
            }
          />
        )}
        <span className="text-sm">{linha.name ?? 'Sem nome no WhatsApp'}</span>
        <Badge tone={spec.tone === 'warning' ? 'warning' : pronto ? 'success' : 'neutral'}>
          {spec.label}
        </Badge>
      </div>

      {pronto ? null : <p className="text-2xs text-muted-foreground">{spec.efeito}</p>}

      {linha.kind === 'SEM_CONVITE' && podeBuscar ? (
        <div className="flex flex-col gap-1">
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<RefreshCw />}
            loading={buscando}
            loadingLabel="Buscando"
            aria-label={`Buscar convite de ${linha.name ?? 'grupo sem nome'}`}
            onClick={() => onBuscar(linha.groupId)}
            className="max-md:w-full md:self-start"
          >
            Buscar convite
          </Button>
          <span className="text-2xs text-muted-foreground">{INVITE_WHY_REFRESH}</span>
        </div>
      ) : null}
    </div>
  );
}

/* ========================================================================== */
/*  A1 — prévias de robô, à parte dos cliques                                  */
/* ========================================================================== */

/**
 * Uma linha, ao lado do resumo por origem: quantas vezes o WhatsApp/Facebook buscou o cartão.
 *
 * Não é clique, e a frase diz isso. Carregando = esqueleto do tamanho da linha; erro = a
 * frase de que não sabemos (nunca um "0" que pareceria contado — invariante 8).
 */
function PreviasNoPeriodo({
  dados,
  carregando,
  erro,
}: {
  dados: RedirectorPreviewHits | undefined;
  carregando: boolean;
  erro: boolean;
}) {
  if (carregando) return <Skeleton className="h-4 w-2/3" />;
  return (
    <p className="text-2xs text-muted-foreground" title={POR_QUE_PREVIAS}>
      {erro || dados === undefined
        ? 'Não conseguimos contar as prévias do link agora.'
        : `Últimos 30 dias: ${previasEmTexto(dados)}`}
    </p>
  );
}

/* ========================================================================== */
/*  B1 — o link de teste                                                       */
/* ========================================================================== */

/**
 * Gera um link que abre o redirecionador SEM contar clique, sem abrir sessão e sem reservar
 * vaga — e mostra para qual grupo um clique de verdade iria. Vale 15 minutos.
 *
 * O token é do servidor; a tela só pede, mostra e deixa copiar. O horário de validade sai no
 * fuso do workspace.
 */
function LinkDeTeste({ workspaceId, redirectorId }: { workspaceId: string; redirectorId: string }) {
  const gerar = useCreateRedirectorTestLink();
  const [gerado, setGerado] = useState<RedirectorTestLink | null>(null);

  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <h3 className="text-md font-medium">5. Testar o link sem contar clique</h3>
      <p className="text-sm text-muted-foreground">
        Abra o link de teste no celular: ele mostra para qual grupo um clique de verdade iria, sem
        gravar clique e sem segurar vaga. Vale por 15 minutos.
      </p>
      {gerar.isError ? <FormAlert>{humanizeError(gerar.error)}</FormAlert> : null}
      {gerado === null ? (
        <Button
          variant="secondary"
          loading={gerar.isPending}
          loadingLabel="Gerando"
          onClick={() =>
            gerar.mutate({ workspaceId, redirectorId }, { onSuccess: (dados) => setGerado(dados) })
          }
        >
          Gerar link de teste
        </Button>
      ) : (
        <>
          <CopyBlock value={gerado.url} what="Link de teste" block />
          <p className="text-2xs text-muted-foreground">
            Não cole este link em anúncio nem em grupo: ele expira e não conta como clique.
          </p>
          <Button variant="ghost" size="sm" onClick={() => setGerado(null)}>
            Gerar outro
          </Button>
        </>
      )}
    </section>
  );
}
